import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
  Divider,
  Checkbox,
  FormGroup,
  Chip,
  useMediaQuery,
  Theme,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  MealItemCustomization,
  MealItemCustomizationOption,
} from '../../../app/data/models/mealItems/mealItemCustomization';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../app/utils/formatters';
import { CartItemCustomization } from '../../../app/data/models/cart';
import { generateCartItemId } from '../cart/cartHelper';
import QuantitySelector from '../../../app/components/QuantitySelector';

interface Props {
  open: boolean;
  onClose: () => void;
  customizations: MealItemCustomization[];
  onAddToCart: (
    selectedCustomizations: CartItemCustomization[],
    cartItemId: string,
    quantity: number
  ) => void;
  mealItemId: string;
  itemName: string;
  itemPrice: number;
}

interface SelectedOption {
  id: string;
  quantity: number;
}

interface CustomizationSelection {
  id: string;
  options: SelectedOption[];
}

export default function MealItemCustomizationSelector({
  open,
  onClose,
  customizations,
  onAddToCart,
  mealItemId,
  itemName,
  itemPrice,
}: Props) {
  const { t } = useTranslation();
  const [selections, setSelections] = useState<CustomizationSelection[]>([]);
  const [canProceed, setCanProceed] = useState(false);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    // Initialize selections with default values based on minimum requirements
    const initialSelections = customizations.map((customization) => ({
      id: customization.id,
      options: customization.rules?.minimumQuantity
        ? [{ id: customization.options[0].id, quantity: 1 }]
        : [],
    }));
    setSelections(initialSelections);
  }, [customizations]);

  useEffect(() => {
    // Validate if all required selections are made
    const isValid = customizations.every((customization, index) => {
      const currentSelection = selections[index]?.options || [];
      const totalQuantity = currentSelection.reduce(
        (sum, opt) => sum + opt.quantity,
        0
      );
      const { minimumQuantity = 0 } = customization.rules || {};
      return totalQuantity >= minimumQuantity;
    });
    setCanProceed(isValid);
  }, [selections, customizations]);

  const handleSingleSelection = (customizationId: string, optionId: string) => {
    setSelections((prev) =>
      prev.map((selection) =>
        selection.id === customizationId
          ? { ...selection, options: [{ id: optionId, quantity: 1 }] }
          : selection
      )
    );
  };

  const handleQuantityChange = (
    customizationId: string,
    optionId: string,
    delta: number
  ) => {
    setSelections((prev) =>
      prev.map((selection) => {
        if (selection.id !== customizationId) return selection;

        const customization = customizations.find(
          (c) => c.id === customizationId
        )!;
        const currentOptions = selection.options;
        const optionIndex = currentOptions.findIndex((o) => o.id === optionId);

        let newOptions = [...currentOptions];
        if (optionIndex === -1 && delta > 0) {
          newOptions.push({ id: optionId, quantity: 1 });
        } else if (optionIndex !== -1) {
          const newQuantity = currentOptions[optionIndex].quantity + delta;
          if (newQuantity <= 0) {
            newOptions = newOptions.filter((o) => o.id !== optionId);
          } else {
            newOptions[optionIndex] = {
              ...newOptions[optionIndex],
              quantity: newQuantity,
            };
          }
        }

        // Validate against rules
        const { maximumQuantity, multipleSelection } =
          customization.rules || {};
        const newTotalQuantity = newOptions.reduce(
          (sum, opt) => sum + opt.quantity,
          0
        );
        if (!multipleSelection && newOptions.length > 1) return selection;
        if (maximumQuantity && newTotalQuantity > maximumQuantity)
          return selection;

        return { ...selection, options: newOptions };
      })
    );
  };

  const handleAddToCart = () => {
    const cartCustomizations: CartItemCustomization[] = selections.map(
      (selection) => {
        const customization = customizations.find(
          (c) => c.id === selection.id
        )!;
        return {
          id: customization.id,
          name: customization.customerInstruction,
          options: selection.options.map((opt) => {
            const option = customization.options.find((o) => o.id === opt.id)!;
            return {
              id: option.id,
              name: option.name,
              price: option.price,
              quantity: opt.quantity,
            };
          }),
        };
      }
    );

    const cartItemId = generateCartItemId(mealItemId, cartCustomizations);

    onAddToCart(cartCustomizations, cartItemId, quantity);
  };

  const renderSelectionControl = (
    customization: MealItemCustomization,
    index: number
  ) => {
    const { rules } = customization;
    const isMultiSelect = rules?.multipleSelection;
    const maxSelections = rules?.maximumQuantity || 1;

    if (maxSelections === 1) {
      return (
        <RadioGroup
          value={selections[index]?.options[0]?.id || ''}
          onChange={(e) =>
            handleSingleSelection(customization.id, e.target.value)
          }
        >
          {customization.options.map((option) => (
            <FormControlLabel
              key={option.id}
              value={option.id}
              control={<Radio />}
              label={renderOptionLabel(option)}
            />
          ))}
        </RadioGroup>
      );
    }

    if (isMultiSelect) {
      return (
        <Box>
          {customization.options.map((option) => {
            const selectedOption = selections[index]?.options.find(
              (o) => o.id === option.id
            );
            const quantity = selectedOption?.quantity || 0;

            return (
              <Box
                key={option.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                {renderOptionLabel(option)}
                <QuantitySelector
                  quantity={quantity}
                  onQuantityChange={(value) =>
                    handleQuantityChange(customization.id, option.id, value)
                  }
                />
              </Box>
            );
          })}
        </Box>
      );
    }

    return (
      <FormGroup>
        {customization.options.map((option) => (
          <FormControlLabel
            key={option.id}
            control={
              <Checkbox
                checked={
                  !!selections[index]?.options.find((o) => o.id === option.id)
                }
                onChange={(e) =>
                  handleCheckboxChange(
                    customization.id,
                    option.id,
                    e.target.checked
                  )
                }
                disabled={
                  !selections[index]?.options.find((o) => o.id === option.id) &&
                  getSelectedCount(customization.id) >= maxSelections
                }
              />
            }
            label={renderOptionLabel(option)}
          />
        ))}
      </FormGroup>
    );
  };

  const handleCheckboxChange = (
    customizationId: string,
    optionId: string,
    checked: boolean
  ) => {
    setSelections((prev) =>
      prev.map((selection) =>
        selection.id === customizationId
          ? {
              ...selection,
              options: checked
                ? [...selection.options, { id: optionId, quantity: 1 }]
                : selection.options.filter((o) => o.id !== optionId),
            }
          : selection
      )
    );
  };

  const getSelectedCount = (customizationId: string) => {
    return (
      selections.find((s) => s.id === customizationId)?.options.length || 0
    );
  };

  const renderOptionLabel = (option: MealItemCustomizationOption) => (
    <Box>
      <Typography>{option.name}</Typography>
      <Typography variant="body2" color="text.secondary">
        {formatCurrency(option.price)}
      </Typography>
    </Box>
  );

  const calculateTotalPrice = () => {
    const basePrice = itemPrice;
    const optionsPrice = selections.reduce((total, selection) => {
      return (
        total +
        selection.options.reduce((sum, opt) => {
          const option = customizations
            .find((c) => c.id === selection.id)
            ?.options.find((o) => o.id === opt.id);
          return sum + (option?.price || 0) * opt.quantity;
        }, 0)
      );
    }, 0);
    return basePrice + optionsPrice;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))}
      sx={{
        '& .MuiDialog-paper': {
          m: 0, // Remove margin on mobile
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: 1,
          borderColor: 'divider',
          px: { xs: 2, sm: 3 },
          position: 'sticky',
          top: 0,
          bgcolor: 'background.paper',
          zIndex: 1,
        }}
      >
        <IconButton onClick={onClose} edge="start">
          <CloseIcon />
        </IconButton>
        <Typography variant="h6" component="div" sx={{ flex: 1 }}>
          {itemName}
        </Typography>
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          flex: 1,
          px: { xs: 2, sm: 3 },
          py: 1,
        }}
      >
        {/* Customizations */}
        {customizations.map((customization, index) => (
          <Box key={customization.id} sx={{ my: 1 }}>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h6">
                  {customization.customerInstruction}
                </Typography>
                {(customization.rules?.minimumQuantity || 0) > 0 && (
                  <Chip
                    label="Required"
                    size="small"
                    sx={{
                      bgcolor: 'grey.100',
                      color: 'text.secondary',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                    }}
                  />
                )}
              </Box>
              <Typography
                color="text.secondary"
                variant="body2"
                sx={{
                  mt: 0.5,
                  mb: 2,
                }}
              >
                {customization.rules?.minimumQuantity
                  ? t('customization.choose-min', {
                      count: customization.rules.minimumQuantity,
                    })
                  : t('customization.choose-up-to', {
                      count: customization.rules?.maximumQuantity || 1,
                    })}
              </Typography>
            </Box>
            {renderSelectionControl(customization, index)}
            {index < customizations.length - 1 && <Divider sx={{ my: 1 }} />}
          </Box>
        ))}
      </DialogContent>

      {/* Footer */}
      <Box sx={{ position: 'relative', width: '100%' }}>
        <Box
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            p: 2,
            bgcolor: 'white',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'center', sm: 'center' },
            gap: 2,
          }}
        >
          {/* Quantity Selector */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              bgcolor: 'white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              p: 0.75,
              zIndex: 1,
              width: { xs: 'fit-content', sm: 'auto' },
            }}
          >
            <Typography variant="subtitle2" sx={{ px: 1 }}>
              {t('customization.quantity')}:
            </Typography>
            <IconButton
              size="small"
              onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Typography sx={{ minWidth: '24px', textAlign: 'center' }}>
              {quantity}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setQuantity((prev) => prev + 1)}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Add to Cart Button */}
          <Button
            onClick={handleAddToCart}
            variant="contained"
            disabled={!canProceed}
            fullWidth
          >
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    textAlign: 'center',
                  }}
                >
                  {t('common.add-to-cart', { quantity })}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    color: 'white',
                    fontSize: 24,
                    fontWeight: 'bold',
                    px: 0.25,
                  }}
                >
                  •
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  textAlign: 'center',
                }}
              >
                {formatCurrency(calculateTotalPrice() * quantity)}
              </Typography>
            </Box>
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
